// Tab - ContributionGrid
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Typography, useTheme, Tooltip } from '@mui/material';
import {
  type GridEventListener,
  type GridColumns,
  type GridValidRowModel,
  type GridRowParams,
  type MuiEvent,
  type GridSortModel,
} from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { getOrganizationContribution } from 'service/ContributionService';
import { colorTheme } from '../../../theme';
import { CustomDataGrid } from 'components';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { type IEmployeeProps } from 'types';
import { strings } from 'i18n/Strings';
import { useSelector, useDispatch } from 'react-redux';
import { CurrencyFormater } from 'components/helpers/commonFunctions/CurrencyFormater';
import {
  setCurrentPage,
  setSortBy,
  setSortOrder,
} from 'store/component/pagination';
import { checkSortingValues } from 'utils/GeneralUtil';

type GridRowData = Record<string, unknown>;
const ContributionGrid = (): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { orgId } = useParams();
  const currentTheme = useTheme();
  const colors = colorTheme(currentTheme.palette.mode);
  const [contributionData, setContributionData] = useState<
    IEmployeeProps[] | unknown
  >([]);
  const [pageCount, setPageCount] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const pageInfo = useSelector((state) => state?.pagination?.contributions);
  const getOrgContributionData = (): void => {
    if (orgId) {
      getOrganizationContribution(
        orgId,
        Number(pageInfo?.page) + 1,
        Number(pageInfo?.pageSize),
        pageInfo?.sortBy,
        pageInfo?.sortOrder,
        searchValue,
      )
        .then((res: unknown) => {
          if (typeof res !== 'undefined') {
            const { basicInfo, count } = res as {
              basicInfo: IEmployeeProps[];
              count: number;
            };
            if (basicInfo) {
              setPageCount(count);
              setContributionData(basicInfo);
            }
          }
        })
        .catch((err: Error) => {
          throw err;
        });
    }
  };

  useEffect(() => {
    getOrgContributionData();
  }, [
    pageInfo?.page,
    pageInfo?.pageSize,
    searchValue,
    pageInfo?.sortBy,
    pageInfo?.sortOrder,
  ]);

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'id',
      flex: 0.3,
      sortable: true,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.userId}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Tooltip title={row.id} placement="top">
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {row.id}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'firstName',
      flex: 0.5,
      sortable: true,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.nameColumn}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Tooltip title={row.name} placement="top">
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {row.firstName} {row.lastName}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'employeeContribution',
      flex: 0.4,
      align: 'right',
      headerAlign: 'center',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.employeeContribution}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Tooltip title={row.employeeContribution} placement="top">
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {CurrencyFormater(Number(row.employeeContribution))}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'employerContribution',
      flex: 0.4,
      align: 'right',
      headerAlign: 'center',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.employerContribution}
        </Typography>
      ),
      renderCell: ({ row }) => {
        return (
          <Tooltip title={row.employerContribution} placement="top">
            <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
              {CurrencyFormater(Number(row.employerContribution))}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'total_contribution',
      flex: 0.4,
      align: 'right',
      headerAlign: 'center',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.totalContribution}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Tooltip title={row.totalContribution} placement="top">
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {CurrencyFormater(Number(row.totalContribution))}
          </Typography>
        </Tooltip>
      ),
    },
  ];

  const handleEvent: GridEventListener<'rowClick'> = (
    params: GridRowParams, // GridRowParams
    event: MuiEvent<React.MouseEvent<HTMLElement>>, // MuiEvent<React.MouseEvent<HTMLElement>>
  ) => {
    const { id }: { id: number } = params?.row;
    navigate(`/employee-contribution-details/${id}`, {
      state: {
        from: `${location.pathname}${
          location.search ? location.search : '?tab=3'
        }`,
      },
    });
  };

  const handleSearch = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    setSearchValue(event.target.value);
  };

  const handleSortChange = (model: GridSortModel): void => {
    dispatch(
      setSortBy({
        key: 'contributions',
        value: model[0].field,
      }),
    );
    dispatch(setSortOrder({ key: 'contributions', value: model[0].sort }));
    dispatch(setCurrentPage({ key: 'contributions', value: 0 }));
  };

  return (
    <>
      <Box>
        <CustomDataGrid
          title="Contribution"
          columns={columns}
          rows={contributionData as GridRowData[]}
          onRowClick={handleEvent}
          getRowId={(row: any) => `${String(row.id)}`}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
          }
          currentPage="contributions"
          pageCount={pageCount}
          searchEnabled={true}
          searchPlaceholder={strings.displayText.searchLabel2}
          handleSearch={handleSearch}
          sortingMode={strings.displayText.sortingServer}
          onSortModelChange={(mode: any) => {
            handleSortChange(mode);
          }}
          sortModel={checkSortingValues(pageInfo)}
        />
      </Box>
    </>
  );
};

export default ContributionGrid;
